<template>
  <div class="rounded-2xl border border-gray-200 bg-white p-3 flex flex-col gap-2">
    <!-- Header: title + overall delta + metric picker -->
    <div class="flex items-center justify-between gap-2 flex-wrap">
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-gray-800">מגמת התקדמות</span>
        <span
          v-if="points.length >= 2"
          class="text-xs font-semibold px-1.5 py-0.5 rounded-full"
          :style="{ color: deltaColor(delta), backgroundColor: deltaColor(delta) + '1a' }"
        >{{ delta > 0 ? '+' : '' }}{{ delta }}</span>
      </div>
      <button
        type="button"
        class="flex items-center gap-1.5 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700"
        @click="openMetricPicker"
      >
        <span>{{ selectedMetricLabel }}</span>
        <i class="pi pi-chevron-down text-[10px] text-gray-400" />
      </button>
    </div>

    <!-- Chart -->
    <svg :viewBox="`0 0 ${VB_W} ${VB_H}`" class="w-full" style="height: auto;">
      <!-- gridlines -->
      <g>
        <line v-for="g in gridLines" :key="'g' + g" :x1="L" :x2="R" :y1="yAt(g)" :y2="yAt(g)" stroke="#f1f5f9" stroke-width="0.8" />
        <text v-for="g in gridLines" :key="'gl' + g" :x="L - 3" :y="yAt(g) + 2" font-size="6" text-anchor="end" fill="#cbd5e1">{{ g }}</text>
      </g>

      <!-- note annotations (vertical dashed guides + flag) -->
      <g v-for="(p, i) in noteMarkers" :key="'n' + i">
        <line :x1="p.x" :y1="T - 6" :x2="p.x" :y2="B" stroke="#f59e0b" stroke-width="0.8" stroke-dasharray="2 2" opacity="0.55" />
        <circle :cx="p.x" :cy="T - 6" r="2.6" fill="#f59e0b" />
      </g>

      <!-- line -->
      <polyline v-if="points.length >= 2" :points="polyline" fill="none" stroke="#8864ee" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" />

      <!-- points -->
      <g v-for="(p, i) in points" :key="'p' + i">
        <text :x="p.x" :y="p.y - 5" font-size="7" text-anchor="middle" font-weight="600" :fill="scoreColor(p.v)">{{ p.v }}</text>
        <circle :cx="p.x" :cy="p.y" r="3" :fill="scoreColor(p.v)" stroke="white" stroke-width="1" />
        <circle :cx="p.x" :cy="p.y" r="10" fill="transparent" style="cursor: pointer" @click="emit('select', p.scan)" />
        <text :x="p.x" y="166" font-size="7" text-anchor="middle" fill="#9ca3af">{{ shortDate(p.scan.createdAt) }}</text>
      </g>
    </svg>

    <!-- Note legend (what changed and when) -->
    <div v-if="noteMarkers.length" class="flex flex-col gap-1 pt-1 border-t border-gray-50">
      <div
        v-for="(p, i) in noteMarkers"
        :key="'leg' + i"
        class="flex items-start gap-2 text-xs cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
        @click="emit('select', p.scan)"
      >
        <i class="pi pi-flag-fill text-amber-500 mt-0.5" style="font-size: 0.6rem" />
        <span class="text-gray-400">{{ shortDate(p.scan.createdAt) }}</span>
        <span class="text-gray-700 flex-1">{{ p.note }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import { DateTime } from 'luxon';
import { useDrawer } from '@torim-app/shared-components';
import type { ISkinScan, SkinConcernEnum } from '@torim-app/types';
import { skinConcernOrder, skinConcernLabels } from '@torim-app/types';
import { scoreColor, deltaColor } from './skin-scan.utils';
import SkinMetricPicker from './SkinMetricPicker.vue';

const props = defineProps({
  scans: { type: Array as PropType<ISkinScan[]>, required: true },
});
const emit = defineEmits<{ (e: 'select', scan: ISkinScan): void }>();

const { openDrawer, closeDrawer } = useDrawer();

type Metric = 'overall' | SkinConcernEnum;
const metric = ref<Metric>('overall');
const metricOptions = computed(() => [
  { value: 'overall' as Metric, label: 'ציון כללי' },
  ...skinConcernOrder.map(c => ({ value: c as Metric, label: skinConcernLabels[c] })),
]);
const selectedMetricLabel = computed(
  () => metricOptions.value.find(o => o.value === metric.value)?.label ?? 'ציון כללי',
);

const openMetricPicker = () => {
  openDrawer(
    SkinMetricPicker,
    {
      options: metricOptions.value,
      selected: metric.value,
      onSelect: (value: Metric) => {
        metric.value = value;
        closeDrawer();
      },
    },
    { title: 'בחירת מדד', closable: true },
  );
};

// SVG geometry
const VB_W = 320;
const VB_H = 176;
const L = 16;
const R = 306;
const T = 18;
const B = 150;
const xAt = (i: number, n: number) => (n <= 1 ? (L + R) / 2 : L + ((R - L) * i) / (n - 1));
const yAt = (v: number) => B - (Math.max(0, Math.min(100, v)) / 100) * (B - T);
const gridLines = [0, 25, 50, 75, 100];

const ordered = computed(() =>
  [...props.scans].sort((a, b) => new Date(a.createdAt as any).getTime() - new Date(b.createdAt as any).getTime()),
);

const valueOf = (s: ISkinScan): number => {
  if (metric.value === 'overall') return s.overallScore;
  return s.concerns?.find(c => c.concern === metric.value)?.score ?? 0;
};

const points = computed(() => {
  const arr = ordered.value;
  return arr.map((s, i) => ({
    scan: s,
    x: xAt(i, arr.length),
    y: yAt(valueOf(s)),
    v: valueOf(s),
    note: (s.notes || '').trim(),
  }));
});
const polyline = computed(() => points.value.map(p => `${p.x},${p.y}`).join(' '));
const noteMarkers = computed(() => points.value.filter(p => p.note));
const delta = computed(() => {
  const ps = points.value;
  return ps.length < 2 ? 0 : Math.round(ps[ps.length - 1].v - ps[0].v);
});

const shortDate = (d: Date | string | undefined) => (d ? DateTime.fromISO(String(d)).toFormat('d/M') : '');
</script>
